import styles from "./Comments.module.css";
import { Button, Checkbox, TextField, FormControlLabel } from "@mui/material";
import { useSnackbar } from "notistack";
import Icon from "@mdi/react";
import { mdiChevronLeft } from "@mdi/js";

// React
import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { useHistory } from "react-router-dom";
import Foco from "react-foco";

// Components
import VotesIcon from "./VotesIcon";
import ItemComment from "./ItemComment";
import AdminPanel from "./AdminPanel";
import StatusTitle from "components/StatusTitle";
import FeatureTitleAndDevDays from "components/FeatureTitleAndDevDays";
import FeatureChangeLog from "./FeatureChangeLog";

// Tools
import Services from "tools/Services";
import GetStatus from "tools/getStatus";

// context
import { useContext } from "react";
import { MyContext } from "components/RoleContext";
import { Role } from "tools/Enums";

export default function PageComments() {
	const [feature, setFeature] = useState({});
	const [comments, setComments] = useState([]);
	const [newComment, setNewComment] = useState({ value: "", intern: false });
	const [error, setError] = useState(false);
	const { id } = useParams();
	const history = useHistory();
	const { role } = useContext(MyContext);
	const { enqueueSnackbar } = useSnackbar();
	const [editingDescription, setEditingDescription] = useState(false);
	const [editingTitle, setEditingTitle] = useState(false);

	// loads feature and comments states when the component mounts
	useEffect(() => {
		loadFeature(id);
		loadComments(id);
	}, [id]);

	// TODO: get this 2 functions (loadFeature & loadComments) into 1
	const loadFeature = async (id) => {
		// GET
		let res = await Services.get("/Feature/getFeature/" + id);

		// ERROR
		if (res.status !== 200) {
			console.error(res);
			return;
		}

		// SUCCES
		setFeature(res.data);
	};
	const loadComments = async (id) => {
		// GET
		let res = await Services.get("/Comment/getComments/" + id);

		// ERROR
		if (res.status !== 200) {
			console.error(res);
			return;
		}

		//SUCCESS
		setComments(res.data);
	};

	// handles
	const handleComment = async (e) => {
		e.preventDefault();
		if (newComment.value === "") {
			setError(true);
			return;
		}

		const sendComment = {
			idFeature: feature.idFeature,
			comment: newComment.value,
			intern: newComment.intern,
		};

		// PUT
		let res = await Services.put("/Comment/setComment", sendComment);

		// ERROR
		if (res.status !== 200) {
			console.error(res);
			return;
		}

		// SUCCESS
		setNewComment({ value: "", intern: false });
		loadComments(id);
	};

	//handle changes in newComment (value & checkbox)
	const handleChangeValue = (e) => {
		setError(false);
		setNewComment({ ...newComment, value: e.target.value });
	};
	const handleChangeCheckbox = (e) => {
		setNewComment({ ...newComment, intern: e.target.checked });
	};

	const onDelete = async (id) => {
		// DELETE
		let res = await Services.delete("/Comment/deleteComment/" + id);

		// ERROR
		if (res.status !== 200) {
			console.error(res);
			return;
		}

		// SUCCESS
		setComments(comments.filter((comment) => comment.idComment !== id));
		enqueueSnackbar("Comment deleted", {
			variant: "success",
		});
	};

	// function passed to child component, set new comment state and update DB
	const onEdit = async (commentEdited) => {
		// PUT
		let res = await Services.put("/Comment/setComment", commentEdited);

		// ERROR
		if (res.status !== 200) {
			console.error(res);
			return;
		}

		// SUCCESS
		setComments(
			comments.map((comment) => {
				if (comment.idComment === commentEdited.idComment) {
					return { ...comment, comment: commentEdited.comment };
				}
				return comment;
			})
		);
	};

	//passed to child component (admin panel)
	const handleChangeUpdateFeature = async (dropDownItem, type) => {
		let value = dropDownItem.id;
		// cast to bool only published atribute
		if (type === "published") value = !!dropDownItem.id;
		const updatedFeature = {
			...feature,
			[type]: value,
		};
		updateFeature(updatedFeature);
	};
	const updateFeature = async (updatedFeature) => {
		// PUT
		let res = await Services.put("/Feature", updatedFeature);

		// ERROR
		if (res.status !== 200) {
			console.error(res);
			return;
		}

		//SUCCESS
		setFeature(updatedFeature);
	};
	const handleDescription = () => {
		setEditingDescription(true);
	};

	const handleEditingFinishOutside = () => {
		setEditingDescription(false);
		updateFeature(feature);
	};
	const handleChange = (e) => {
		setFeature({ ...feature, [e.target.name]: e.target.value });
	};
	const handleEditingFinish = (e) => {
		if (e.key === "Enter") {
			setEditingDescription(false);
			updateFeature(feature);
		}
	};

	const handleTitle = () => {
		setEditingTitle(true);
	};
	const handleEditingFinishOutsideTitle = () => {
		setEditingTitle(false);
		updateFeature(feature);
	};

	const handleEditingFinishTitle = (e) => {
		if (e.key === "Enter") {
			setEditingTitle(false);
			updateFeature(feature);
		}
	};

	return (
		<div className={styles.container}>
			<Button variant="outlined" className={styles.button} onClick={history.goBack}>
				<Icon path={mdiChevronLeft} size={1} /> Back
			</Button>
			<div className={styles.section}>
				<div className={styles.publicWraper}>
					<div className={styles.header}>
						{role === Role.Admin && (
							<>
								<VotesIcon nbVotes={feature.nbVotes} />
								<div className={styles.containerCopy}>
									{!editingTitle && (
										<div
											title={feature.title}
											className={styles.titleCopy}
											onClick={role === Role.Admin && handleTitle}
										>
											{feature.title}
										</div>
									)}
									{editingTitle && (
										<Foco onClickOutside={handleEditingFinishOutsideTitle}>
											<TextField
												multiline
												fullWidth
												type="text"
												name="title"
												value={feature.title}
												onChange={handleChange}
												onKeyDown={handleEditingFinishTitle}
											/>
										</Foco>
									)}
								</div>
							</>
						)}
						{role === Role.User && (
							<>
								<FeatureTitleAndDevDays feature={feature} />
								<StatusTitle name={GetStatus.getName(feature.status)} status={feature.status} />
							</>
						)}
					</div>

					{!editingDescription && (
						<p className={styles.description} onClick={role === Role.Admin && handleDescription}>
							{feature.description}
						</p>
					)}
					{editingDescription && (
						<Foco onClickOutside={handleEditingFinishOutside}>
							<TextField
								multiline
								fullWidth
								type="text"
								name="description"
								value={feature.description}
								onChange={handleChange}
								// onKeyDown={handleEditingFinish}
							/>
						</Foco>
					)}

					{comments.length === 0 && (
						<p className={styles.noCommentsYet}>There aren't comments yet, be the first!</p>
					)}
					{/* TODO: sort by date or should backend give it to me sorted? */}
					{comments.map((comment) => {
						return (
							<ItemComment
								key={comment.idComment}
								comment={comment}
								onDelete={onDelete}
								onEdit={onEdit}
							/>
						);
					})}
					<hr className={styles.hr} />
					<form onSubmit={handleComment} className={styles.form}>
						<TextField
							error={error}
							fullWidth
							helperText={error && "Your messagge is empty"}
							variant="outlined"
							className={styles.textField}
							value={newComment.value}
							onChange={handleChangeValue}
							label="Share your opinion"
						/>
						<Button type="submit">Comment</Button>
					</form>
					<FormControlLabel
						label="Keep your comment private"
						control={<Checkbox checked={newComment.intern} onChange={handleChangeCheckbox} />}
					/>
					{role === Role.Admin && <FeatureChangeLog status={feature.status} />}
				</div>
				{feature.idFeature && (
					<AdminPanel feature={feature} onChange={handleChangeUpdateFeature} updateFeature={updateFeature} />
				)}
			</div>
		</div>
	);
}
